import {ReactKeycloakProvider} from '@react-keycloak/web';
import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import Keycloak from 'keycloak-js';
import {
    Admin,
    Datagrid,
    DateField,
    EmailField,
    FunctionField, NumberField,
    Resource,
    TextField,
    useRecordContext
} from "react-admin";
import {AbholgebietList, AbholgebietShow} from "./abholgebiete";
import {AdresseList, AdresseShow} from "./adressen";
import {AnmeldungList, AnmeldungShow} from "./anmeldungen";
import {AbholungList, AbholungListStatistics, AbholungShow} from "./abholungen";
import KeycloakAuthProvider from "./providers/KeycloakAuthProvider";
import ApiDataProvider from "./providers/ApiDataProvider";
import {HydraAdmin, ResourceGuesser} from "@api-platform/admin";
import {Layout} from "./layout";
import {Box, Card, CardContent, CardHeader, Grid, Paper, Typography, LinearProgress} from "@mui/material";
import {
    DeleteSweep,
    LocationOn,
    People,
    LocalShipping,
    TrackChanges,
    CalendarToday as CalendarTodayIcon,
    Chair,
    ElectricalServices,
    ContactMail,
    Mail,
    Phone
} from "@mui/icons-material";
import Dashboard from "./dashboard";
import {useGetOne, localStorageStore} from 'react-admin';

import {Loading, Error} from 'react-admin';
import { makeStyles } from '@mui/styles';

const eventLogger = (event: unknown, error: unknown) => {
    console.log('onKeycloakEvent', event, error)
}

const rootElement = document.getElementById('root');

if (!rootElement)
    throw "Could not find root element";

// https://www.npmjs.com/package/@react-keycloak/web
let keycloakConfig = {
    "realm": "",
    "url": "",
    "clientId": "backoffice-react-app",
}, keyCloakInitOptions = {
    onLoad: "login-required",
}, apiUrl = "";

if (process.env.NODE_ENV === 'development') {
    // use environment from .env when running in local development server
    keycloakConfig.realm = process.env.REACT_APP_REALM ?? "";
    keycloakConfig.url = process.env.REACT_APP_AUTH_SERVER_URL ?? "";
    apiUrl = process.env.REACT_APP_API_URL ?? "";
} else {
    // use external environment when running in docker
    keycloakConfig.realm = rootElement.dataset.realm ?? "";
    keycloakConfig.url = rootElement.dataset.authServerUrl ?? "";
    apiUrl = rootElement.dataset.apiUrl ?? "";
}

console.log('Using Keycloak config', keycloakConfig);
console.log('Using API Endpoint ' + apiUrl);

const keycloak = new Keycloak(keycloakConfig),
    authProvider = new KeycloakAuthProvider(keycloak),
    dataProvider = ApiDataProvider(keycloak, apiUrl);

const store = localStorageStore();
store.setItem('apiUrl', apiUrl);
store.setItem('sidebar.open', false);


const root = createRoot(rootElement!);

root.render(
        <ReactKeycloakProvider
            authClient={keycloak}
            onEvent={eventLogger}
            LoadingComponent={<strong>Loading</strong>}
            initOptions={keyCloakInitOptions}>
            <React.StrictMode>
                <HydraAdmin
                    entrypoint={apiUrl}
                    dataProvider={dataProvider}
                    authProvider={authProvider}
                    layout={Layout}
                    dashboard={Dashboard}
                    disableTelemetry
                    store={store}
                >
                    <ResourceGuesser name="regionalbezeichnungen"
                                     options={{role: 'admin', label: 'Regionalbezeichnungen'}}/>
                    <ResourceGuesser name="abholgebiet-ortsteile"
                                     options={{role: 'admin', label: 'Abholgebiet Ortsteile'}}/>
                    <ResourceGuesser name="abholgebiet-mengen" options={{role: 'admin', label: 'Abholgebiet Mengen'}}/>

                    <ResourceGuesser name="bundeslaender" options={{role: 'admin', label: 'Bundesländer'}}/>
                    <ResourceGuesser name="regierungsbezirke" options={{role: 'admin', label: 'Regierungsbezirke'}}/>
                    <ResourceGuesser name="regionen" options={{role: 'admin', label: 'Regionen'}}/>
                    <ResourceGuesser name="kreise" options={{role: 'admin', label: 'Kreise'}}/>
                    <ResourceGuesser name="landkreise" options={{role: 'admin', label: 'Landkreise'}}/>
                    <ResourceGuesser name="gemeindeverbaende" options={{role: 'admin', label: 'Gemeindeverbände'}}/>
                    <ResourceGuesser name="gemeinden" options={{role: 'admin', label: 'Gemeinden'}}/>
                    <ResourceGuesser name="gemeindeteile" options={{role: 'admin', label: 'Gemeindeteile'}}/>

                    <ResourceGuesser name="quellen" options={{role: 'admin', label: 'Quellen'}}/>
                    <ResourceGuesser name="abfallarten" options={{
                        role: 'admin',
                        label: 'Abfallarten'
                    }} /*list={AbfallartList} show={AbfallartShow}*/ icon={DeleteSweep}/>

                    <Resource name="abholgebiete" options={{role: 'admin', label: 'Abholgebiete'}} list={AbholgebietList}
                              show={AbholgebietShow} icon={TrackChanges}/>

                    <Resource name="strassen" options={{label: 'Adressen'}} list={AdresseList} show={AdresseShow}
                              icon={LocationOn}/>
                    <Resource name="anmeldungen" options={{label: 'Anmeldungen'}} list={AnmeldungList} show={AnmeldungShow}
                              icon={People}/>
                    <Resource name="abholtermine" options={{label: 'Abholungen'}} list={AbholungList} show={AbholungShow}
                              icon={LocalShipping}/>
                </HydraAdmin>
            </React.StrictMode>
        </ReactKeycloakProvider>
);

const a_months = new Array('Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember');
const a_weekdays = new Array('Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag');

const AbfallartIcon = (props) => {
    const record = useRecordContext({...props});
    var abfallartId;

    //accept abfallart object or id
    if (typeof record.abfallart.id == 'undefined') {
        switch (record.abfallart) {
            case '/v2/abfallarten/5':
                abfallartId = 5;
                break;
            case '/v2/abfallarten/6':
                abfallartId = 6;
                break;
            default:
                abfallartId = 0;
                break;
        }
    } else {
        abfallartId = record.abfallart.id;
    }

    switch (abfallartId) {
        case 5:
            return (
                <Chair sx={{fontSize: '58px', color: '#fff'}}/>
            );
        case 6:
            return (
                <ElectricalServices sx={{fontSize: '58px', color: '#fff'}}/>
            );
        default:
            return (
                <Box></Box>
            );
    }
};

const PaperContactMail = (props) => {
    const record = useRecordContext({...props});

    return (
        <Paper elevation={3} sx={{
            p: 2,
            background: '#fff',
            color: '#222',
            textAlign: 'left',
            marginBottom: '30px'
        }}>
            <Grid container spacing={1}>
                <Grid item xs={1}>
                    <ContactMail sx={{marginTop: '0px', color: '#B83BA1'}}/>
                </Grid>
                <Grid item xs={11}>
                    <Typography variant={"body1"}>
                        <b>{record.vorname} {record.nachname}</b><br/>
                        {record.strasse} {record.hausnummer}<br/>
                        {record.plz} {record.ort}
                    </Typography>
                    <Grid container spacing={1}
                          sx={{borderTop: '1px solid #ccc', marginTop: '15px', paddingTop: '10px'}}>
                        <Grid item xs={1}>
                            <Mail sx={{marginTop: '0px'}} color="info"/>
                        </Grid>
                        <Grid item xs={11}>
                            <Typography variant={"body2"} sx={{paddingTop: '2px'}}>
                                {record.email}
                            </Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Phone sx={{marginTop: '0px'}} color="info"/>
                        </Grid>
                        <Grid item xs={11}>
                            <Typography variant={"body2"} sx={{paddingTop: '2px'}}>
                                {record.telefon}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

const PaperCalendarDay = (props) => {
    const record = useRecordContext({...props});

    if (record.abholdatum || record.termin) {
        var oDate = new Date(String(record.abholdatum ? record.abholdatum : record.termin));
        var tempDate = oDate.toISOString().split('T')[0];
        var year = tempDate.split('-')[0];
        var month = a_months[parseInt(tempDate.split('-')[1]) - 1];
        var day = tempDate.split('-')[2];
        var weekday = a_weekdays[oDate.getDay()];

        return (
            <Paper elevation={3} sx={{
                p: 2,
                background: '#fff',
                color: '#222',
                textAlign: 'left',
                marginBottom: '30px'
            }}>
                <Grid container spacing={3}>
                    <Grid item xs={2}>
                        <CalendarTodayIcon sx={{marginTop: '0px'}} color="primary" fontSize="large"/>
                    </Grid>
                    <Grid item xs={10}>
                        <Typography variant={"h4"}>
                            {day}
                        </Typography>
                        <Typography variant={"h6"} sx={{textTransform: 'uppercase'}}>
                            {month}
                        </Typography>
                        <Typography variant={"h6"}>
                            {year}
                        </Typography>
                        <Typography variant={"body2"} sx={{
                            textTransform: 'uppercase',
                            paddingTop: '15px',
                            borderTop: '1px solid #ccc',
                            marginTop: '15px'
                        }}>
                            {weekday}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        )
    } else {
        return (
            <Paper elevation={3} sx={{
                p: 2,
                background: '#fff',
                color: '#222',
                textAlign: 'left',
                marginBottom: '30px'
            }}>
                <Grid container spacing={3}>
                    <Grid item xs={2}>
                        <CalendarTodayIcon sx={{marginTop: '0px', color: '#1e448f'}} fontSize="large"/>
                    </Grid>
                    <Grid item xs={10}>
                        <Typography variant={"h6"}
                                    sx={{textTransform: 'uppercase', color: 'error.main', paddingTop: '5px'}}>
                            ohne Termin
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        )
    }
};

const date2DateString = function (date) {
    if (!date)
        return '--';

    var oDate = new Date(String(date));
    var tempDate = oDate.toISOString().split('T')[0];
    var year = tempDate.split('-')[0];
    var month = tempDate.split('-')[1];
    var day = tempDate.split('-')[2];
    var formattedDate = day + '.' + month + '.' + year;

    return formattedDate;
}

const date2DateTimeString = function (date) {
    if (!date)
        return '--';

    var oDate = new Date(String(date));
    var tempDate = oDate.toISOString().split('T')[0];
    var tempTime = oDate.toISOString().split('T')[1];
    var year = tempDate.split('-')[0];
    var month = tempDate.split('-')[1];
    var day = tempDate.split('-')[2];
    var hour = tempTime.split(':')[0];
    var min = tempTime.split(':')[1];
    var formattedDate = day + '.' + month + '.' + year + ', ' + hour + ':' + min + ' Uhr';

    return formattedDate;
}

const date2WeekdayDateString = function (date) {
    if (!date)
        return '--';

    var oDate = new Date(String(date));
    var tempDate = oDate.toISOString().split('T')[0];
    var year = tempDate.split('-')[0];
    var month = a_months[oDate.getMonth()];
    var day = tempDate.split('-')[2];
    var weekday = a_weekdays[oDate.getDay()];
    var formattedDate = weekday + ', ' + day + '. ' + month + ' ' + year;

    return formattedDate;
}

const ClearFloat = () => {
    return <Box sx={{clear: 'both'}}></Box>
}

const GetAbfallart = (record) => {
    const {data, isLoading, error} = useGetOne(
        'abfallarten',
        { id: record.abfallart }
    );

    if (isLoading) return <InlineLoading/>;
    if (error) return null;
    if (!data) return null;

    return(data.name);
}

const InlineLoading = () => {
    return (
        <Box sx={{ width: '100%' }}>
            <LinearProgress />
        </Box>
    )
}

const themeStyles = makeStyles({
    // primary rmg blue
    // main: 0664A6
    // light: 5391d8
    // dark: 003a77

    // theme anmeldung pink
    // main: A6179D

    // theme abholung blue
    // main: 176AA6

    /*
    primary #0461a5
    secondary #385ba8
    tertiary #a6179d
    error #B3261E
    outline #79747E
    green #006e18
     */

    title: {
        flex: 1,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    spacer: {
        flex: 1,
    },
    bo_nav_header: {
        letterSpacing: '.5px'
    },
    primary_bg: {
        backgroundColor: '#1e448f',
        background: 'linear-gradient(145deg, #2E68DB 11%, #132B5C 75%)'
    },
    anmeldung_bg: {
        backgroundColor: '#B83BA1 !important'
    },
    abholung_bg: {
        backgroundColor: '#2872b7 !important'
    }
});

export {AbfallartIcon, PaperContactMail, PaperCalendarDay, GetAbfallart, date2DateString, date2DateTimeString, date2WeekdayDateString, ClearFloat, InlineLoading, themeStyles};